import { ref, unref, computed, watch } from 'vue'
import store from '@/store'

/**
 * columnDefs に含められない columnState キー
 */
const STATES_CANNOT_INCLUDE_IN_DEFS = [
  'rowGroup',
  'rowGroupIndex',
  'pivot',
  'pivotIndex',
]

/**
 * ユーザーリストとして表示するカラムの state 利用
 * @param {MaybeRef.<GridApi>} gridApi grid操作API
 * @param {RefLike.<ColDef[]>} columnDefs カラム定義データ
 */
export const useUserListColumnState = ({ gridApi, columnDefs }) => {
  /**
   * 初期 columnStates
   * @type {ColumnState[]}
   */
  const _initialColumnStates = store.state.userList.columnStates
  /**
   * 初期 columnStates の Map
   * @type {Map.<string, ColumnState>}
   */
  const _initialColumnStateMap = new Map(
    _initialColumnStates.map((s) => [s.colId, s])
  )
  /**
   * 初期 columnStates のインデックスの Map
   * @type {Map.<string, number>}
   */
  const _initialColumnStateIndexMap = new Map(
    _initialColumnStates.map((s, index) => [s.colId, index])
  )
  /**
   * 初期カラムstateをマージしたカラム定義
   * @type {ComputedRef.<ColDef[]>}
   */
  const columnDefsWithInitialState = computed(() =>
    (unref(columnDefs) || [])
      .map((d) => {
        if (!_initialColumnStateMap.has(d.colId)) return d
        const state = Object.fromEntries(
          Object.entries(_initialColumnStateMap.get(d.colId)).filter(
            ([key]) => !STATES_CANNOT_INCLUDE_IN_DEFS.includes(key)
          )
        )
        return { ...d, ...state }
      })
      .sort(
        (a, b) =>
          _initialColumnStateIndexMap.get(a.colId) -
          _initialColumnStateIndexMap.get(b.colId)
      )
  )

  /**
   * カラムstate
   * @type {Ref.<ColumnState[]>}
   */
  const columnStates = ref(_initialColumnStates)
  /**
   * カラムstate を更新する
   * @param {ColumnState[]} state カラムstate
   */
  const updateColumnStates = (states) => {
    columnStates.value = states
  }
  // columnState を store に保存
  watch(columnStates, (value) => {
    store.commit('updateUserListState', { columnStates: value })
  })

  /**
   * カラムにソートが指定されている場合はtrue
   * @type {ComputedRef.<boolean>}
   */
  const hasSort = computed(() => columnStates.value.some((s) => s.sort != null))
  /**
   * カラムのソートをクリアする
   */
  const clearSort = () =>
    unref(gridApi)?.applyColumnState({ defaultState: { sort: null } })

  /**
   * カラムの表示状態
   * @type {ComputedRef.<Object>}
   */
  const columnVisible = computed(() =>
    Object.fromEntries(
      columnStates.value.map((state) => [state.colId, !state.hide])
    )
  )
  /**
   * visible の変更を columnState に反映する
   * @param {{ colId: string; visible: boolean; }[]} value カラム表示状態変更値
   */
  const applyColumnVisible = (value) => {
    const newStates = [...columnStates.value]
    const colIdsToMoveLast = []
    for (const { colId, visible } of value) {
      const targetState = newStates.find((s) => s.colId === colId)
      // 非表示 => 表示に変化したカラムを末尾に移動するために保持しておく
      if (visible && targetState.hide !== !visible) {
        colIdsToMoveLast.push(colId)
      }
      targetState.hide = !visible
    }

    // MEMO: 表示を切り替えるカラムの数が多いと処理が重いので一旦ブラウザに処理を返してから実行する
    setTimeout(() => {
      unref(gridApi)?.applyColumnState({ state: newStates })
      if (colIdsToMoveLast.length) {
        const toIndex = newStates.length - colIdsToMoveLast.length
        unref(gridApi)?.moveColumns(colIdsToMoveLast, toIndex)
      }
    }, 0)
  }

  return {
    columnDefsWithInitialState,
    columnStates,
    updateColumnStates,
    hasSort,
    clearSort,
    columnVisible,
    applyColumnVisible,
  }
}
